import { defineStore } from 'pinia';
import { Translations } from 'vue3-gettext';
import gettext, { availableLocales, defaultLanguage } from './gettext';
import { connection } from '@vp/vue-components';

const languages = import.meta.glob<{ default: Translations }>('../language/*.json', { eager: false });

type Language = 'en' | 'fr' | 'es' | 'de' | 'it';

type LanguageState = {
  translations: Translations;
  forceLanguage?: Language;
  current?: Language;
};

export const useLanguageStore = defineStore('language', {
  state: (): LanguageState => ({
    translations: {},
  }),

  actions: {
    async loadLanguage(lang: Language) {
      const translations = (await languages[`../language/${lang}.json`]()).default;

      this.translations[lang] = translations[lang];
      gettext.translations = this.translations;
    },

    async setLanguage(lang?: string, force: boolean = false) {
      // Resolved before the first await: the render hooks call this without
      // awaiting it, so by the time the catalogue has loaded the active pinia
      // may belong to another SSR request.
      const connectionStore = connection.useConnectionStore();
      const requested = lang?.split('-')[0] ?? defaultLanguage;
      const locale = (availableLocales.includes(requested) ? requested : defaultLanguage) as Language;

      if (force) {
        this.forceLanguage = locale;
      }

      if (typeof this.forceLanguage !== 'undefined' && locale !== this.forceLanguage) {
        console.warn(`You're trying to set language to ${lang} but it's forced to ${this.forceLanguage}`);
        return;
      }

      // Sent to the API as X-VP-Locale. Both are recorded ahead of the catalogue
      // load: a request fired right after a switch has to carry the new locale,
      // and `current` is what initConnectionStore falls back to on client-side
      // navigation — navigating mid-switch would otherwise put the previous
      // language back on the wire, where nothing would correct it.
      connectionStore.setLocale(locale);
      this.current = locale;

      // `gettext` is module state, so it is empty on the client even though the hydrated store has the catalogue.
      if (typeof this.translations[locale] === 'undefined') {
        await this.loadLanguage(locale);
      } else {
        gettext.translations = this.translations;
      }
      gettext.current = locale;
    },
  },
});
